import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
  },
  section: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    marginBottom: 20,
    color: '#2c3e50',
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 10,
    color: '#2c3e50',
    borderBottomWidth: 1,
    borderBottomColor: '#3498db',
    paddingBottom: 5,
  },
  jobTitle: {
    fontSize: 14,
    marginBottom: 5,
    color: '#2c3e50',
  },
  company: {
    fontSize: 12,
    marginBottom: 5,
    color: '#3498db',
  },
  duration: {
    fontSize: 10,
    marginBottom: 5,
    color: '#7f8c8d',
  },
  description: {
    fontSize: 10,
    color: '#34495e',
    marginLeft: 15,
    marginBottom: 3,
  },
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  skillCategory: {
    fontSize: 12,
    color: '#2c3e50',
    marginBottom: 5,
    marginTop: 10,
  },
  skillItem: {
    padding: 5,
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
    fontSize: 10,
    marginRight: 10,
    marginBottom: 10,
  },
  certificationItem: {
    fontSize: 10,
    color: '#34495e',
    marginLeft: 15,
    marginBottom: 3,
  },
});

const ResumePDF = ({ data }) => {
  if (!data) {
    return null;
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{data.name}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Education</Text>
          {data.education.map((edu, index) => (
            <View key={index} style={{ marginBottom: 15 }}>
              <Text style={styles.jobTitle}>{edu.degree} in {edu.field}</Text>
              <Text style={styles.company}>{edu.institution}</Text>
              <Text style={styles.duration}>{edu.year}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Professional Experience</Text>
          {data.experience.map((exp, index) => (
            <View key={index} style={{ marginBottom: 15 }}>
              <Text style={styles.jobTitle}>{exp.role}</Text>
              <Text style={styles.company}>{exp.company}</Text>
              <Text style={styles.duration}>{exp.period}</Text>
              {exp.description.map((desc, i) => (
                <Text key={i} style={styles.description}>• {desc}</Text>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Skills</Text>
          {data.skills.map((skillCategory, index) => (
            <View key={index}>
              <Text style={styles.skillCategory}>{skillCategory.category}</Text>
              <View style={styles.skillsGrid}>
                {skillCategory.items.map((skill, i) => (
                  <View key={i} style={styles.skillItem}>
                    <Text>{skill}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Certifications</Text>
          {data.certifications.map((cert, index) => (
            <Text key={index} style={styles.certificationItem}>• {cert}</Text>
          ))}
        </View>
      </Page>
    </Document>
  );
};

ResumePDF.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    education: PropTypes.arrayOf(PropTypes.shape({
      institution: PropTypes.string.isRequired,
      degree: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      year: PropTypes.string.isRequired,
    })).isRequired,
    experience: PropTypes.arrayOf(PropTypes.shape({
      company: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      period: PropTypes.string.isRequired,
      description: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
    skills: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
    certifications: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
};

export default ResumePDF;
